import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Roulette from "@/components/Roulette";
import { Sparkles, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { RARITY_LABEL, RARITY_CLASS } from "@/lib/rarity";

export default function DeckReveal({ order, onDone }) {
  const navigate = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [deck, setDeck] = useState([]);  // shuffled positions of 33 card ids
  const [picked, setPicked] = useState([]); // positions clicked
  const [results, setResults] = useState(null); // backend results array
  const [revealed, setRevealed] = useState([]); // positions already flipped
  const [spinAll, setSpinAll] = useState(false); // "Reclamar todas" disparado
  const [guardadas, setGuardadas] = useState(new Set()); // card_instance_id guardados para depois
  const [phase, setPhase] = useState("picking"); // picking | revealing | spinning | done

  useEffect(() => {
    // Initial shuffled positions of card ids 1..33
    const ids = Array.from({length: 33}, (_, i) => i + 1);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }
    setDeck(ids);
  }, [order.order_id]);

  const canPick = phase === "picking" && picked.length < order.quantity;

  const togglePick = (idx) => {
    if (phase !== "picking") return;
    if (picked.includes(idx)) {
      setPicked(picked.filter(p => p !== idx));
    } else if (picked.length < order.quantity) {
      setPicked([...picked, idx]);
    }
  };

  const confirmDraw = async () => {
    if (picked.length !== order.quantity) return;
    try {
      const card_ids = picked.map(i => deck[i]);
      const r = await api.post(`/orders/${order.order_id}/draw`, { card_ids });
      setResults(r.data.results);
      setPhase("revealing");
      // flip them one by one
      for (let i = 0; i < picked.length; i++) {
        await new Promise(res => setTimeout(res, 700));
        setRevealed(prev => [...prev, picked[i]]);
      }
      // After flips: mostra a grelha de prémios pendentes (guardar/reclamar) se houver algum
      setTimeout(() => {
        const hasPrize = r.data.results.some(x => x.won_prize && x.card_instance_id);
        setPhase(hasPrize ? "spinning" : "done");
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a revelar");
    }
  };

  const allPrizes = (results || []).filter(r => r.won_prize && r.card_instance_id);
  const pendingPrizes = allPrizes.filter(r => !guardadas.has(r.card_instance_id));
  const allDecided = allPrizes.length > 0 && allPrizes.every(r => r.spun || guardadas.has(r.card_instance_id));

  useEffect(() => {
    if (phase === "spinning" && allDecided) setPhase("done");
  }, [phase, allDecided]);

  const onMiniRouletteFinish = (cardInstanceId, value) => {
    setResults(prev => prev.map(r => r.card_instance_id === cardInstanceId ? { ...r, value_eur: value, spun: true } : r));
    refreshUser(); // saldo mudou no backend (voucher_balance) — recarrega o utilizador global
  };

  const guardar = (cardInstanceId) => {
    setGuardadas(prev => new Set(prev).add(cardInstanceId));
  };

  const guardarTodas = () => {
    setGuardadas(prev => {
      const next = new Set(prev);
      pendingPrizes.forEach(r => { if (!r.spun) next.add(r.card_instance_id); });
      return next;
    });
  };

  const totalWon = useMemo(() => (results || []).reduce((s, r) => s + (r.value_eur || 0), 0), [results]);

  return (
    <main className="pt-28 pb-20 max-w-7xl mx-auto px-4 md:px-8">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center justify-center gap-2"><Sparkles size={14}/> Pedido #{order.order_id}</p>
        {phase === "picking" && (
          <>
            <h1 className="font-display text-5xl md:text-6xl mb-2">Escolhe {order.quantity} carta{order.quantity > 1 ? "s" : ""}</h1>
            <p className="text-[color:var(--text-2)]">Selecionadas <span className="text-gold">{picked.length}/{order.quantity}</span> · Clica nas cartas para escolher</p>
          </>
        )}
        {phase === "revealing" && <h1 className="font-display text-5xl md:text-6xl mb-2">A revelar…</h1>}
        {phase === "spinning" && (
          <>
            <h1 className="font-display text-5xl md:text-6xl mb-2">Guarda ou reclama os prémios</h1>
            <p className="text-[color:var(--text-2)]">
              {allPrizes.filter(r => r.spun || guardadas.has(r.card_instance_id)).length}/{allPrizes.length} decididos ·
              reclama já ou guarda para a tua coleção e decide mais tarde
            </p>
          </>
        )}
        {phase === "done" && (
          <>
            <h1 className="font-display text-5xl md:text-6xl mb-2">Ritual concluído</h1>
            <p className="text-gold font-display text-3xl mt-2 flex items-center justify-center gap-2"><Trophy size={24}/> {totalWon.toFixed(2)}€ em vouchers</p>
            {guardadas.size > 0 && (
              <p className="text-[color:var(--text-2)] mt-2">{guardadas.size} carta{guardadas.size > 1 ? "s" : ""} guardada{guardadas.size > 1 ? "s" : ""} na tua coleção, por reclamar</p>
            )}
          </>
        )}
      </div>

      {/* Deck grid */}
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-11 gap-2 md:gap-3">
        {deck.map((cardId, idx) => {
          const isPicked = picked.includes(idx);
          const isRevealed = revealed.includes(idx);
          const result = isRevealed && results ? results[picked.indexOf(idx)] : null;
          const dimmed = phase !== "picking" && !isPicked;
          return (
            <div key={idx} className={`pick-tile ${isPicked ? "picked" : ""} ${dimmed ? "disabled" : ""} ${!canPick && !isPicked ? "disabled" : ""}`}
              onClick={() => togglePick(idx)} data-testid={`deck-card-${idx}`}>
              <div className="tarot-card perspective">
                <motion.div className="relative w-full h-full preserve-3d"
                  animate={{ rotateY: isRevealed ? 180 : 0 }}
                  transition={{ duration: 0.8, type: "spring", stiffness: 90, damping: 14 }}>
                  <div className="absolute inset-0 backface-hidden">
                    <img src="/cards/back.jpg" alt="" className="card-img float-glow" />
                  </div>
                  <div className="absolute inset-0 backface-hidden rotate-y-180">
                    <img src={`/cards/card-${String(cardId).padStart(2,'0')}.jpg`} alt="" className="card-img"
                      style={result?.won_prize ? {boxShadow: "0 0 24px rgba(212,175,55,0.6)"} : {}} />
                  </div>
                </motion.div>
              </div>
              {result && (
                <div className={`mt-1 text-center text-[10px] md:text-xs uppercase tracking-wide px-1 py-0.5 rounded border ${RARITY_CLASS[result.rarity]}`}>
                  {RARITY_LABEL[result.rarity]}{result.won_prize ? " · 🏆" : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {phase === "picking" && (
        <div className="mt-10 text-center">
          <button className="btn-primary text-lg" disabled={picked.length !== order.quantity} onClick={confirmDraw} data-testid="confirm-draw">
            Revelar {picked.length === order.quantity ? "agora" : `(faltam ${order.quantity - picked.length})`}
          </button>
        </div>
      )}

      {phase === "spinning" && (
        <div className="mt-10">
          {pendingPrizes.length > 1 && (
            <div className="text-center mb-6 flex gap-3 justify-center">
              <button className="btn-primary" disabled={spinAll} onClick={() => setSpinAll(true)} data-testid="spin-all-button">
                {spinAll ? "A reclamar todas…" : `Reclamar todas (${pendingPrizes.length})`}
              </button>
              <button className="btn-secondary" disabled={spinAll} onClick={guardarTodas} data-testid="guardar-all-button">
                Colecionar todas ({pendingPrizes.length})
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {pendingPrizes.map(r => (
              <div key={r.card_instance_id}>
                <Roulette cardInstanceId={r.card_instance_id} card={r} compact autoSpin={spinAll}
                  onFinish={(value) => onMiniRouletteFinish(r.card_instance_id, value)} />
                {!r.spun && !spinAll && (
                  <button className="btn-secondary w-full mt-2 !py-1 text-xs" onClick={() => guardar(r.card_instance_id)}
                    data-testid={`guardar-button-${r.card_instance_id}`}>
                    Guardar para depois
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="mt-10 flex gap-3 justify-center">
          <button className="btn-primary" onClick={() => navigate("/premios")} data-testid="go-prizes">Trocar por prémios</button>
          <button className="btn-secondary" onClick={() => navigate("/minha-conta")} data-testid="go-collection">Ver coleção</button>
          <button className="btn-secondary" onClick={() => onDone && onDone()} data-testid="back-to-sorteio">Voltar</button>
        </div>
      )}
    </main>
  );
}
